/*
 * DER GUIDE IST FUER PARTNER STANDARDMAESSIG OFFEN (Rafael, 23.09.2026 18:38Z:
 * „mach den GUIDE modus auch mehr sichtbar und selbsterklaerend - mach ihn auch
 * standardmaessig an").
 *
 * Bis hierher galt fuer ALLE dieselbe Regel: offen nur, wenn im Browser
 * `guide-modus = an` stand. Ein Partner, der den Knopf nie gedrueckt hatte, landete
 * deshalb nach der Anmeldung im rohen Arbeitsbereich und hat den Guide nie gesehen.
 *
 * Neu, NUR fuer angemeldete Nicht-Admins (Rafaels eigene Sicht bleibt, wie sie war):
 *   1. Hat der Partner den Guide selbst geoeffnet oder geschlossen, gilt SEINE Wahl —
 *      gemerkt je Nutzer (`guide-modus:<userId>`), nicht je Browser fuer alle, damit
 *      zwei Partner am selben Rechner einander nichts vorgeben.
 *   2. Sonst gilt die Vorgabe, die Rafael je Partner in users.json setzen kann
 *      (`guideStandard: 'aus'`); fehlt sie, ist der Guide OFFEN.
 * Gemerkt wird nur ein bewusster Klick (Knopf oder Schliessen im Guide), nie der
 * Anfangszustand — sonst wuerde die Vorgabe beim ersten Laden als „Wahl" eingefroren
 * und eine spaetere Vorgabe von Rafael kaeme nie mehr an.
 */

export type GuideStandard = 'an' | 'aus';

/** Was diese Datei vom angemeldeten Nutzer braucht (Teilmenge von AuthUser). */
export interface GuideNutzer {
  id: string;
  role: string;
  guideStandard?: GuideStandard;
}

/** Nur das, was von localStorage gebraucht wird — damit Tests einen eigenen Speicher geben koennen. */
export interface GuideSpeicher {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Der bisherige, browserweite Merker — gilt unveraendert fuer Rafael (Admin) und ohne Anmeldung. */
export const GUIDE_MERKER_ALLGEMEIN = 'guide-modus';

export function guideMerkerFuer(userId: string): string {
  return `guide-modus:${userId}`;
}

export function erklaerkarteMerkerFuer(userId: string): string {
  return `guide-erklaerkarte-zu:${userId}`;
}

/** Angemeldeter Nicht-Admin — nur fuer ihn gilt die neue Vorgabe. */
export function istPartnerAnsicht(authEnabled: boolean | null | undefined, user: GuideNutzer | null | undefined): user is GuideNutzer {
  return authEnabled === true && !!user && user.role !== 'admin';
}

function lies(speicher: GuideSpeicher | null | undefined, key: string): string | null {
  if (!speicher) return null;
  try { return speicher.getItem(key); } catch { return null; }
}

/** Ob der Guide beim Laden der Oberflaeche offen ist. */
export function guideAnfangsZustand(args: {
  fernAnsicht: boolean;
  touchInput?: boolean;
  authEnabled: boolean | null | undefined;
  user: GuideNutzer | null | undefined;
  speicher: GuideSpeicher | null | undefined;
}): boolean {
  if (args.fernAnsicht) return true;
  if (istPartnerAnsicht(args.authEnabled, args.user)) {
    const gemerkt = lies(args.speicher, guideMerkerFuer(args.user.id));
    if (gemerkt === 'an') return true;
    if (gemerkt === 'aus') return false;
    return args.user.guideStandard !== 'aus';
  }
  const remembered = lies(args.speicher, GUIDE_MERKER_ALLGEMEIN);
  return remembered === 'an' || (remembered === null && args.touchInput === true);
}

/** Merkt die BEWUSSTE Wahl eines Partners. Fuer alle anderen ein No-op (dort merkt App.tsx wie bisher). */
export function merkeGuideWahl(args: {
  authEnabled: boolean | null | undefined;
  user: GuideNutzer | null | undefined;
  offen: boolean;
  speicher: GuideSpeicher | null | undefined;
}): void {
  if (!istPartnerAnsicht(args.authEnabled, args.user) || !args.speicher) return;
  try {
    args.speicher.setItem(guideMerkerFuer(args.user.id), args.offen ? 'an' : 'aus');
  } catch (err) {
    // Kein stilles Schlucken: die Wahl gilt fuer diese Sitzung trotzdem, nur nicht nach dem Neuladen.
    console.warn('[Guide] Wahl konnte nicht gemerkt werden:', err);
  }
}
